import type {
  AcabamentoDaCaixa,
  Bloco,
  ConfigEscrita,
  EstiloEscrita,
  Formato,
  Paleta,
  TamanhoTexto,
} from '@miolos/core';
import { ENTRELINHA_PADRAO, cssDaPauta, linhasQueCabem, separadorEnfeitado, tamanhoFontePt } from '@miolos/core';
import {
  alinhamentoCss,
  bordaDoTipo,
  corDoPapel,
  fundoDoPreenchimento,
  pesoNumerico,
  type Fontes,
} from './cores';
import { GlifoMotivo } from './GlifoMotivo';
import { MiniCalendario } from './MiniCalendario';
import { GradeComData } from './GradeComData';

export interface ContextoBloco {
  paleta: Paleta;
  formato: Formato;
  fontes: Fontes;
  escala: number;
  trackingTitulos: number;
  /** Resolve a URL das imagens (uploads da API). */
  resolverImagem?: (url: string) => string;
  /**
   * Enfeites de linha escolhidos para o miolo. É o que faz os separadores de
   * seção ganharem o mesmo desenho das linhas (corações, dentinhos...).
   */
  escrita?: ConfigEscrita;
  /**
   * Acabamento do título das caixas do dia (filete, faixa...).
   *
   * Vem do miolo porque atravessa o desenho inteiro; ausente é o título solto de
   * sempre.
   */
  acabamentoDaCaixa?: AcabamentoDaCaixa;
}

const CLASSES_PAUTA: Record<EstiloEscrita, string> = {
  LINHA: 'pauta-linhas',
  PONTILHADO: 'pauta-pontilhada',
  QUADRICULADO: 'pauta-quadriculada',
  PONTOS: 'pauta-pontos',
  SEM_LINHA: '',
};

function variaveisPauta(ctx: ContextoBloco, passo: number, malha: number, traco = 0.16): React.CSSProperties {
  return {
    '--passo': `${passo}mm`,
    '--traco': `${traco}mm`,
    '--cor-linha': ctx.paleta.linha,
    '--malha': `${malha}mm`,
    '--ponto': '0.16mm',
  } as React.CSSProperties;
}

/**
 * Altura, em milímetros, da linha de um título em tamanho `XS`.
 *
 * É o tamanho do desenho que acompanha o título da caixa: desenhar com a altura da
 * linha (e não com um número fixo) é o que impede o ícone de empurrar a linha e
 * espremer o conteúdo da caixa.
 */
export function alturaDaLinhaDoTituloMm(formato: Formato, tamanho: TamanhoTexto, escala: number): number {
  return Number((tamanhoFontePt(formato, tamanho, escala) * 0.3528 * ENTRELINHA_PADRAO[tamanho]).toFixed(2));
}

export function BlocoImpresso({ bloco, ctx }: { bloco: Bloco; ctx: ContextoBloco }) {
  const { paleta, formato } = ctx;

  switch (bloco.tipo) {
    /* ------------------------------ texto ------------------------------ */
    case 'TEXTO': {
      const tamanho = tamanhoFontePt(formato, bloco.tamanho, ctx.escala, bloco.tracking ?? 0);
      return (
        <p
          style={{
            margin: `${bloco.espacoAcimaMm ?? 0}mm 0 ${bloco.espacoAbaixoMm ?? 0}mm`,
            fontFamily: bloco.tamanho === 'DISPLAY' || bloco.tamanho === 'XL' ? ctx.fontes.titulo : ctx.fontes.corpo,
            fontSize: `${tamanho}pt`,
            fontWeight: pesoNumerico(bloco.peso),
            color: corDoPapel(paleta, bloco.cor),
            textAlign: alinhamentoCss(bloco.alinhamento),
            textTransform: bloco.maiusculas ? 'uppercase' : 'none',
            letterSpacing: bloco.tracking ? `${bloco.tracking}em` : undefined,
            lineHeight: bloco.entrelinha ?? ENTRELINHA_PADRAO[bloco.tamanho],
            fontStyle: bloco.italico ? 'italic' : 'normal',
            whiteSpace: 'pre-wrap',
            marginInline: 0,
          }}
        >
          {bloco.texto}
        </p>
      );
    }

    /* ------------------------------ linhas ----------------------------- */
    case 'LINHAS': {
      const passo = bloco.alturaLinhaMm + bloco.espacamentoMm;
      const classe = CLASSES_PAUTA[bloco.estilo];
      const rotuloPt = tamanhoFontePt(formato, 'XS', ctx.escala);

      // Linhas desenhadas em SVG: traço (cheia, ondulada, dupla...) e enfeite.
      const pauta = cssDaPauta(
        {
          estilo: bloco.estilo,
          alturaLinhaMm: bloco.alturaLinhaMm,
          espacamentoMm: bloco.espacamentoMm,
          traco: bloco.traco,
          motivo: bloco.motivo,
          motivoModo: bloco.motivoModo,
          motivoACadaNLinhas: bloco.motivoACadaNLinhas,
          motivoEspacamentoMm: bloco.motivoEspacamentoMm,
          motivoTamanhoMm: bloco.motivoTamanhoMm,
          motivoLado: bloco.motivoLado,
          corMotivo: bloco.corMotivo,
        },
        { linha: paleta.linha, destaque: paleta.destaque },
      );

      const altura = bloco.flexivel ? undefined : `${bloco.alturaMm ?? passo * 6}mm`;

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            flex: bloco.flexivel ? '1 1 auto' : '0 0 auto',
            height: altura,
          }}
        >
          {bloco.rotulo ? (
            <span
              style={{
                fontSize: `${rotuloPt}pt`,
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                fontWeight: 700,
                color: corDoPapel(paleta, bloco.rotuloCor ?? 'TEXTO_SUAVE'),
                marginBottom: '1.2mm',
                display: 'block',
                flex: '0 0 auto',
              }}
            >
              {bloco.rotulo}
            </span>
          ) : null}

          {bloco.comData ? (
            /*
             * Quantas linhas de data desenhar.
             *
             * Quem decide é o motor, que é o único que sabe a altura disponível
             * (a área de impressão nasce com `display: none`, então aqui não há
             * nada para medir). Um bloco com altura declarada ainda dá para
             * calcular; um bloco flexível sem contagem desenha uma linha só —
             * feio de ver, e é o ponto: sobrar espaço é discreto, esconder
             * caixinhas de data pelo `overflow: hidden` não era.
             *
             * Antes o teto de 80 valia sempre: numa folha A4 sobravam 1054px de
             * caixinhas escondidas e a última saía cortada ao meio.
             */
            <GradeComData
              passo={passo}
              colunas={bloco.colunas}
              linhas={bloco.linhas ?? (bloco.alturaMm ? linhasQueCabem(bloco.alturaMm, passo) : 1)}
              desenharLinha={(linha) => (
                <div key={linha} style={{ display: 'flex', alignItems: 'center', gap: '2mm' }}>
                  <span
                    style={{
                      width: `${Math.max(passo * 0.85, 4)}mm`,
                      height: `${Math.max(passo * 0.55, 3)}mm`,
                      border: `0.18mm solid ${paleta.linha}`,
                      borderRadius: '0.5mm',
                      flex: '0 0 auto',
                    }}
                  />
                  <span style={{ flex: '1 1 auto', height: '0.14mm', background: paleta.linha }} />
                </div>
              )}
            />
          ) : (
            <div
              style={{
                flex: '1 1 auto',
                minHeight: 0,
                display: 'grid',
                gridTemplateColumns: bloco.colunas > 1 ? `repeat(${bloco.colunas}, 1fr)` : undefined,
                columnGap: bloco.colunas > 1 ? '5mm' : undefined,
              }}
            >
              {Array.from({ length: Math.max(1, bloco.colunas) }).map((_, coluna) => (
                <div
                  key={coluna}
                  className={classe}
                  style={{ ...variaveisPauta(ctx, passo, passo), ...pauta }}
                />
              ))}
            </div>
          )}
        </div>
      );
    }

    /* ---------------------------- checklist ---------------------------- */
    case 'CHECKLIST': {
      const passo = bloco.alturaLinhaMm;
      const tamanho = Math.max(passo * 0.5, 2.8);
      const fontePt = tamanhoFontePt(formato, 'SM', ctx.escala);

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0, flex: '1 1 auto', minHeight: 0 }}>
          {bloco.rotulo ? (
            <span
              style={{
                fontSize: `${tamanhoFontePt(formato, 'XS', ctx.escala)}pt`,
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                fontWeight: 700,
                color: corDoPapel(paleta, bloco.rotuloCor ?? 'TEXTO_SUAVE'),
                marginBottom: '1.2mm',
              }}
            >
              {bloco.rotulo}
            </span>
          ) : null}
          {bloco.itens.map((item, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: '2mm',
                height: `${passo}mm`,
                flex: '0 0 auto',
              }}
            >
              <span
                style={{
                  width: `${tamanho}mm`,
                  height: `${tamanho}mm`,
                  border: `0.2mm solid ${corDoPapel(paleta, bloco.cor)}`,
                  borderRadius: bloco.marcador === 'CIRCULO' ? '50%' : '0.4mm',
                  flex: '0 0 auto',
                  marginBottom: '0.6mm',
                }}
              />
              <span
                style={{
                  flex: '1 1 auto',
                  fontSize: `${fontePt}pt`,
                  color: corDoPapel(paleta, bloco.cor),
                  borderBottom: `0.15mm dotted ${paleta.linha}`,
                  paddingBottom: '0.4mm',
                  lineHeight: 1.1,
                }}
              >
                {item}
              </span>
            </div>
          ))}
        </div>
      );
    }

    /* ------------------------------ grade ------------------------------ */
    case 'GRADE': {
      const classe = bloco.estilo === 'PONTO' ? 'pauta-pontos' : 'pauta-quadriculada';
      const conteudo = (
        <div
          className={classe}
          style={{
            ...variaveisPauta(ctx, bloco.tamanhoMalhaMm, bloco.tamanhoMalhaMm),
            flex: '1 1 auto',
            minHeight: 0,
            height: bloco.flexivel ? undefined : `${bloco.alturaMm ?? 40}mm`,
          }}
        />
      );

      if (!bloco.rotulo) return conteudo;

      return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: bloco.flexivel ? '1 1 auto' : '0 0 auto', minHeight: 0 }}>
          <span
            style={{
              fontSize: `${tamanhoFontePt(formato, 'XS', ctx.escala)}pt`,
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              fontWeight: 700,
              color: corDoPapel(paleta, bloco.rotuloCor ?? 'TEXTO_SUAVE'),
              marginBottom: '1.2mm',
            }}
          >
            {bloco.rotulo}
          </span>
          {conteudo}
        </div>
      );
    }

    /* ------------------------------ caixa ------------------------------ */
    case 'CAIXA': {
      const borda = bordaDoTipo(paleta, bloco.borda);
      const acabamento = ctx.acabamentoDaCaixa ?? 'SIMPLES';
      const corDaCaixa = corDoPapel(paleta, bloco.corTitulo ?? 'DESTAQUE_TEXTO');
      /*
       * A tarja do topo entra na folga de dentro da caixa. Sem folga (quadro sem
       * caixa, no estilo contemporâneo) não há onde pintar sem passar por cima do
       * título, então ela simplesmente não sai.
       */
      const comTarjaNoTopo = acabamento === 'TOPO' && bloco.preenchimentoMm >= 1;

      return (
        <div
          style={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: '1.4mm',
            background: fundoDoPreenchimento(paleta, bloco.fundo),
            borderWidth: borda.borderWidth,
            borderStyle: borda.borderStyle,
            borderColor: borda.borderColor,
            borderRadius: `${bloco.raioMm}mm`,
            padding: `${bloco.preenchimentoMm}mm`,
            flex: bloco.flexivel ? '1 1 auto' : '0 0 auto',
            height: bloco.alturaMm ? `${bloco.alturaMm}mm` : undefined,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {comTarjaNoTopo ? (
            /*
             * Tarja do topo: camada absoluta, **fora do fluxo** — ela não ocupa
             * altura nenhuma, então a medida da caixa continua a mesma e o
             * conteúdo não pode ser empurrado para fora.
             */
            <span
              aria-hidden
              data-acabamento="TOPO"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '1.5mm',
                background: corDaCaixa,
                borderTopLeftRadius: `${bloco.raioMm}mm`,
                borderTopRightRadius: `${bloco.raioMm}mm`,
              }}
            />
          ) : null}

          {bloco.titulo ? (
            /*
             * Título e desenho na mesma linha.
             *
             * O desenho é medido pela **altura da linha do título** (e não por um
             * tamanho fixo em milímetros): assim a linha do título continua com a
             * mesma altura em qualquer formato e a caixa não precisa de reserva
             * nova — foi o cuidado que o ícone exigiu para não virar mais uma
             * origem de corte em silêncio.
             *
             * O acabamento (filete ou faixa) entra aqui da mesma forma: `position:
             * relative` na linha e o enfeite como camada por trás ou por baixo,
             * **sem preenchimento e sem margem**. Nada disso encolhe a largura em
             * que o motor mede o título, que é o que faria o rótulo virar duas
             * linhas e o conteúdo perder uma.
             */
            <span
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: '1.4mm',
                flex: '0 0 auto',
                textAlign: bloco.alinhamento ? alinhamentoCss(bloco.alinhamento) : 'left',
              }}
            >
              {acabamento === 'FAIXA' ? (
                <span
                  aria-hidden
                  data-acabamento="FAIXA"
                  style={{
                    position: 'absolute',
                    /*
                     * A faixa cresce para os lados até a borda interna da caixa,
                     * passando por cima da folga — é o que faz o título parecer um
                     * selo. Como ela é absoluta, a largura do texto não muda.
                     */
                    left: `${-bloco.preenchimentoMm}mm`,
                    right: `${-bloco.preenchimentoMm}mm`,
                    top: `${-0.6}mm`,
                    bottom: `${-0.6}mm`,
                    background: paleta.destaqueSuave,
                    borderRadius: '1mm',
                  }}
                />
              ) : null}

              {acabamento === 'FILETE' ? (
                <span
                  aria-hidden
                  data-acabamento="FILETE"
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    bottom: '-0.9mm',
                    height: '0.35mm',
                    background: corDaCaixa,
                    opacity: 0.85,
                  }}
                />
              ) : null}

              {bloco.icone ? (
                <GlifoMotivo
                  motivo={bloco.icone}
                  cor={corDaCaixa}
                  tamanhoMm={alturaDaLinhaDoTituloMm(formato, bloco.tamanhoTitulo ?? 'XS', ctx.escala)}
                />
              ) : null}
              <span
                style={{
                  position: 'relative',
                  fontSize: `${tamanhoFontePt(formato, bloco.tamanhoTitulo ?? 'XS', ctx.escala, 0.14)}pt`,
                  textTransform: 'uppercase',
                  letterSpacing: '0.14em',
                  fontWeight: 700,
                  color: corDaCaixa,
                }}
              >
                {bloco.titulo}
              </span>
            </span>
          ) : null}

          {bloco.descricao ? (
            <p
              style={{
                margin: 0,
                fontSize: `${tamanhoFontePt(formato, 'XS', ctx.escala)}pt`,
                lineHeight: 1.35,
                color: paleta.textoSuave,
                flex: '0 0 auto',
              }}
            >
              {bloco.descricao}
            </p>
          ) : null}

          {bloco.conteudo && bloco.conteudo.length > 0 ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1.4mm',
                flex: '1 1 auto',
                minHeight: 0,
              }}
            >
              {bloco.conteudo.map((filho, i) => (
                <BlocoImpresso key={i} bloco={filho} ctx={ctx} />
              ))}
            </div>
          ) : null}
        </div>
      );
    }

    /* ---------------------------- separador ---------------------------- */
    case 'SEPARADOR': {
      const cor = corDoPapel(paleta, bloco.cor);

      /**
       * Quando o miolo tem um desenho escolhido para as linhas (corações,
       * dentinhos, esmaltes...), os separadores de seção ganham o mesmo
       * desenho — é a "linha de demarcação" decorada.
       */
      const enfeitado = ctx.escrita
        ? separadorEnfeitado(ctx.escrita, {
            cor: bloco.cor,
            espessuraMm: bloco.espessuraMm,
            espacoMm: bloco.espacoMm,
          })
        : null;

      const motivo = bloco.motivo ?? enfeitado?.motivo ?? 'NENHUM';
      const tamanhoMotivo = bloco.motivoTamanhoMm ?? enfeitado?.motivoTamanhoMm ?? 3.4;
      const corDoMotivo = bloco.corMotivo ?? enfeitado?.corMotivo ?? null;

      if (bloco.estilo === 'MOTIVO' || motivo !== 'NENHUM') {
        const tom = corDoMotivo ?? cor;
        const linha = { flex: '1 1 auto', height: `${bloco.espessuraMm}mm`, background: cor, opacity: 0.55 };

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '1.8mm',
              margin: `${bloco.espacoMm}mm 0`,
              flex: '0 0 auto',
            }}
          >
            <span style={linha} />
            {[0, 1, 2].map((i) => (
              <GlifoMotivo key={i} motivo={motivo} cor={tom} tamanhoMm={tamanhoMotivo} />
            ))}
            <span style={linha} />
          </div>
        );
      }

      if (bloco.estilo === 'ORNAMENTO') {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '3mm',
              margin: `${bloco.espacoMm}mm 0`,
              flex: '0 0 auto',
            }}
          >
            <span style={{ width: '18mm', height: `${bloco.espessuraMm}mm`, background: cor, opacity: 0.7 }} />
            <span
              style={{
                width: '1.8mm',
                height: '1.8mm',
                background: cor,
                transform: 'rotate(45deg)',
              }}
            />
            <span style={{ width: '18mm', height: `${bloco.espessuraMm}mm`, background: cor, opacity: 0.7 }} />
          </div>
        );
      }

      if (bloco.estilo === 'PONTOS') {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '2mm',
              margin: `${bloco.espacoMm}mm 0`,
              flex: '0 0 auto',
            }}
          >
            {[0, 1, 2].map((i) => (
              <span key={i} style={{ width: '1mm', height: '1mm', borderRadius: '50%', background: cor }} />
            ))}
          </div>
        );
      }

      return (
        <div
          style={{
            flex: '0 0 auto',
            margin: `${bloco.espacoMm}mm 0`,
            borderTopWidth: `${bloco.espessuraMm}mm`,
            borderTopStyle: bloco.estilo === 'TRACEJADA' ? 'dashed' : 'solid',
            borderTopColor: cor,
          }}
        />
      );
    }

    /* ----------------------------- espaço ------------------------------ */
    case 'ESPACO':
      return (
        <div
          style={{
            flex: bloco.flexivel ? '1 1 auto' : '0 0 auto',
            height: bloco.flexivel ? undefined : `${bloco.alturaMm}mm`,
            minHeight: 0,
          }}
        />
      );

    /* ----------------------------- tabela ------------------------------ */
    case 'TABELA': {
      const borda = bordaDoTipo(paleta, bloco.borda);
      const total = bloco.larguras.reduce((soma, valor) => soma + valor, 0) || 1;
      const fontePt = tamanhoFontePt(formato, 'SM', ctx.escala);
      const fonteCabecalhoPt = tamanhoFontePt(formato, 'XS', ctx.escala);

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: bloco.flexivel ? '1 1 auto' : '0 0 auto',
            height: bloco.alturaMm ? `${bloco.alturaMm}mm` : undefined,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {bloco.rotulo ? (
            <span
              style={{
                fontSize: `${fonteCabecalhoPt}pt`,
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                fontWeight: 700,
                color: corDoPapel(paleta, bloco.rotuloCor ?? 'TEXTO_SUAVE'),
                marginBottom: '1.2mm',
                flex: '0 0 auto',
              }}
            >
              {bloco.rotulo}
            </span>
          ) : null}

          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              tableLayout: 'fixed',
              fontFamily: ctx.fontes.corpo,
              color: paleta.texto,
            }}
          >
            <thead>
              <tr>
                {bloco.colunas.map((coluna, i) => (
                  <th
                    key={i}
                    style={{
                      width: `${((bloco.larguras[i] ?? 0) / total) * 100}%`,
                      fontSize: `${fonteCabecalhoPt}pt`,
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      textAlign: 'left',
                      padding: '1.2mm 1.6mm',
                      background: paleta.fundoAlt,
                      borderTopWidth: borda.borderWidth,
                      borderBottomWidth: borda.borderWidth,
                      borderStyle: borda.borderStyle,
                      borderColor: borda.borderColor,
                      fontWeight: 700,
                      color: paleta.textoSuave,
                    }}
                  >
                    {coluna}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: bloco.linhas }).map((_, linha) => (
                <tr key={linha}>
                  {bloco.colunas.map((__, coluna) => (
                    <td
                      key={coluna}
                      style={{
                        height: `${bloco.alturaLinhaMm}mm`,
                        fontSize: `${fontePt}pt`,
                        padding: '0 1.6mm',
                        color: paleta.texto,
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        borderBottomWidth: borda.borderWidth,
                        borderBottomStyle: borda.borderStyle,
                        borderBottomColor: borda.borderColor,
                        borderRightWidth: coluna < bloco.colunas.length - 1 ? borda.borderWidth : '0',
                        borderRightStyle: borda.borderStyle,
                        borderRightColor: borda.borderColor,
                      }}
                    >
                      {bloco.celulas?.[linha]?.[coluna] ?? ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    /* ------------------------- mini calendário ------------------------- */
    case 'MINI_CALENDARIO':
      return (
        <MiniCalendario
          ano={bloco.ano}
          mes={bloco.mes}
          destacar={bloco.destacar}
          mostrarNumeroSemana={bloco.mostrarNumeroSemana}
          rotulo={bloco.rotulo}
          semDatas={bloco.semDatas}
          feriados={bloco.feriados}
          mostrarNomesFeriados={bloco.mostrarNomesFeriados}
          paleta={paleta}
          rotuloCor={corDoPapel(paleta, bloco.rotuloCor ?? 'TEXTO_SUAVE')}
        />
      );

    /* ------------------------------ campos ----------------------------- */
    case 'CAMPOS': {
      const fontePt = tamanhoFontePt(formato, 'SM', ctx.escala);
      const fonteRotuloPt = tamanhoFontePt(formato, 'XS', ctx.escala);
      const cor = corDoPapel(paleta, bloco.cor);

      /**
       * Formulário de papel: o risco ocupa toda a largura e a informação é
       * escrita ABAIXO dele. As máscaras (`(00) 00000-0000`) ficam na ponta
       * direita da linha, em tom leve, como guia para a caneta.
       */
      if (bloco.rotuloAbaixo) {
        const corRotulo = corDoPapel(paleta, bloco.rotuloCor ?? 'TEXTO_SUAVE');
        const corMascara = corDoPapel(paleta, bloco.mascaraCor ?? 'TEXTO_SUAVE');

        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: '1 1 auto',
              minHeight: 0,
              height: `${bloco.itens.length * (bloco.alturaLinhaMm + 3)}mm`,
            }}
          >
            {bloco.itens.map((item, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                  flex: '1 1 auto',
                  minHeight: 0,
                }}
              >
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'space-between',
                    gap: '3mm',
                    fontSize: `${fontePt}pt`,
                    fontWeight: 600,
                    color: cor,
                    padding: '0 0.6mm 0.6mm',
                    lineHeight: 1.1,
                  }}
                >
                  <span style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>{item.valor ?? ''}</span>
                  {item.mascara ? (
                    <span
                      style={{
                        fontSize: `${fonteRotuloPt}pt`,
                        fontWeight: 500,
                        letterSpacing: '0.08em',
                        color: corMascara,
                        opacity: 0.75,
                        whiteSpace: 'nowrap',
                        flex: '0 0 auto',
                      }}
                    >
                      {item.mascara}
                    </span>
                  ) : null}
                </span>

                {/* O risco, de ponta a ponta da área de escrita */}
                <span style={{ display: 'block', width: '100%', height: '0.22mm', background: paleta.linha }} />

                <span
                  style={{
                    fontSize: `${fonteRotuloPt}pt`,
                    color: corRotulo,
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                    fontWeight: 600,
                    paddingTop: '0.7mm',
                  }}
                >
                  {item.rotulo}
                </span>
              </div>
            ))}
          </div>
        );
      }

      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.4mm', flex: '1 1 auto', minHeight: 0 }}>
          {bloco.itens.flatMap((item, i) => {
            const quantidadeLinhas = Math.max(1, item.linhas ?? 1);
            return Array.from({ length: quantidadeLinhas }).map((__, linha) => (
              <div
                key={`${i}-${linha}`}
                style={{ display: 'flex', alignItems: 'flex-end', gap: '2.5mm', minHeight: 0 }}
              >
                <span
                  style={{
                    fontSize: `${fonteRotuloPt}pt`,
                    color: corDoPapel(paleta, bloco.rotuloCor ?? 'TEXTO_SUAVE'),
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    flex: '0 0 auto',
                  }}
                >
                  {linha === 0 ? item.rotulo : ''}
                </span>
                <span
                  style={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    height: `${item.linhas && item.linhas > 1 ? 6 : bloco.alturaLinhaMm}mm`,
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'flex-end',
                    fontSize: `${fontePt}pt`,
                    fontWeight: 600,
                    color: cor,
                    borderBottomWidth: '0.2mm',
                    borderBottomStyle: item.valor ? 'solid' : 'dotted',
                    borderBottomColor: paleta.linha,
                    paddingBottom: '0.5mm',
                    overflow: 'hidden',
                  }}
                >
                  {linha === 0 ? item.valor ?? '' : ''}
                </span>
              </div>
            ));
          })}
        </div>
      );
    }

    /* ------------------------------ grupo ------------------------------ */
    case 'GRUPO': {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: bloco.flexivel ? '1 1 auto' : '0 0 auto',
            height: bloco.alturaMm ? `${bloco.alturaMm}mm` : undefined,
            minHeight: 0,
            gap: '1.6mm',
          }}
        >
          {bloco.rotulo ? (
            <span
              style={{
                fontSize: `${tamanhoFontePt(formato, 'XS', ctx.escala)}pt`,
                textTransform: 'uppercase',
                letterSpacing: '0.14em',
                fontWeight: 700,
                color: corDoPapel(paleta, bloco.rotuloCor ?? 'TEXTO_SUAVE'),
                flex: '0 0 auto',
              }}
            >
              {bloco.rotulo}
            </span>
          ) : null}
          <div
            style={{
              flex: '1 1 auto',
              minHeight: 0,
              display: 'grid',
              gridTemplateColumns: `repeat(${bloco.colunas}, minmax(0, 1fr))`,
              gridAutoRows: 'minmax(0, 1fr)',
              gap: `${bloco.gapMm}mm`,
            }}
          >
            {bloco.blocos.map((filho, i) => (
              <div
                key={i}
                style={{
                  minWidth: 0,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  ...(bloco.centralizarCelulas ? { justifyContent: 'center' } : {}),
                  ...(bloco.celulasVisiveis
                    ? {
                        border: `0.2mm solid ${paleta.borda}`,
                        borderRadius: '1.5mm',
                        padding: `${bloco.preenchimentoCelulaMm ?? 2}mm`,
                        background: paleta.fundoAlt,
                      }
                    : {}),
                }}
              >
                <BlocoImpresso bloco={filho} ctx={ctx} />
              </div>
            ))}
          </div>
        </div>
      );
    }

    /* --------------------------- caligrafia ---------------------------- */
    case 'CALIGRAFIA': {
      const corBase = corDoPapel(paleta, bloco.cor);
      const corGuia = corDoPapel(paleta, bloco.corGuia);

      const guia = (posicao: number, cor: string, espessura: number, tracejada: boolean, chave: string) => (
        <span
          key={chave}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: `${posicao}%`,
            transform: 'translateY(-50%)',
            borderTopWidth: `${espessura}mm`,
            borderTopStyle: tracejada ? 'dashed' : 'solid',
            borderTopColor: cor,
          }}
        />
      );

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {Array.from({ length: bloco.linhas }).map((_, linha) => (
            <div
              key={linha}
              style={{ position: 'relative', height: `${bloco.alturaLinhaMm}mm`, flex: '0 0 auto' }}
            >
              {bloco.estilo === 'QUADRICULADO' ? (
                <div
                  className="pauta-quadriculada"
                  style={{
                    ...variaveisPauta(ctx, bloco.alturaLinhaMm, bloco.alturaLinhaMm, 0.12),
                    position: 'absolute',
                    inset: 0,
                  }}
                />
              ) : bloco.estilo === 'PAUTA_SIMPLES' ? (
                guia(100, corBase, 0.25, false, 'base')
              ) : bloco.estilo === 'PAUTA_DUPLA' ? (
                <>
                  {guia(0, corBase, 0.25, false, 'topo')}
                  {guia(100, corBase, 0.25, false, 'base')}
                </>
              ) : (
                <>
                  {guia(0, corGuia, 0.15, true, 'topo')}
                  {guia(40, corGuia, 0.15, true, 'meio')}
                  {guia(66, corBase, 0.25, false, 'base')}
                  {guia(100, corGuia, 0.15, true, 'desc')}
                </>
              )}
            </div>
          ))}
        </div>
      );
    }

    /* ---------------------------- partitura ---------------------------- */
    case 'PARTITURA': {
      const cor = corDoPapel(paleta, bloco.cor);
      const alturaSistema = bloco.espacamentoLinhaMm * 4;
      const sobra = 1.4;

      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: `${bloco.espacoEntreSistemasMm}mm`,
            flex: '1 1 auto',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {Array.from({ length: bloco.sistemas }).map((_, sistema) => (
            <div
              key={sistema}
              style={{ position: 'relative', height: `${alturaSistema + 0.4}mm`, flex: '0 0 auto' }}
            >
              {Array.from({ length: 5 }).map((__, linha) => (
                <span
                  key={linha}
                  style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: `${linha * bloco.espacamentoLinhaMm}mm`,
                    height: '0.18mm',
                    background: cor,
                  }}
                />
              ))}
              <span
                style={{
                  position: 'absolute',
                  left: 0,
                  top: `${-sobra / 2}mm`,
                  width: '0.25mm',
                  height: `${alturaSistema + sobra}mm`,
                  background: cor,
                }}
              />
              {bloco.barraFinal ? (
                <span
                  style={{
                    position: 'absolute',
                    right: 0,
                    top: `${-sobra / 2}mm`,
                    width: '0.45mm',
                    height: `${alturaSistema + sobra}mm`,
                    background: cor,
                  }}
                />
              ) : null}
            </div>
          ))}
        </div>
      );
    }

    /* ------------------------------ imagem ----------------------------- */
    case 'IMAGEM': {
      const url = ctx.resolverImagem ? ctx.resolverImagem(bloco.url) : bloco.url;
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5mm',
            flex: bloco.flexivel ? '1 1 auto' : '0 0 auto',
            height: bloco.alturaMm ? `${bloco.alturaMm}mm` : undefined,
            minHeight: 0,
          }}
        >
          <div
            style={{
              flex: '1 1 auto',
              minHeight: 0,
              border: bloco.moldura ? `0.25mm solid ${paleta.borda}` : undefined,
              borderRadius: '1.5mm',
              overflow: 'hidden',
              background: '#ffffff',
            }}
          >
            <img
              src={url}
              alt=""
              style={{
                display: 'block',
                width: '100%',
                height: '100%',
                objectFit: bloco.ajuste === 'COBRIR' ? 'cover' : 'contain',
              }}
            />
          </div>
          {bloco.legenda ? (
            <p
              style={{
                margin: 0,
                fontSize: `${tamanhoFontePt(formato, 'XS', ctx.escala)}pt`,
                color: corDoPapel(paleta, bloco.corLegenda),
                textAlign: 'center',
                flex: '0 0 auto',
              }}
            >
              {bloco.legenda}
            </p>
          ) : null}
        </div>
      );
    }

    /* ----------------------------- horários ---------------------------- */
    case 'HORARIOS': {
      const corLinha = corDoPapel(paleta, bloco.corLinha);
      const corRotulo = corDoPapel(paleta, bloco.corRotulo);
      const pt = tamanhoFontePt(formato, 'XS', ctx.escala, 0);

      /*
       * As linhas dividem a altura recebida em partes iguais (`flex: 1 1 0`):
       * a grade das 5h às 23h e a das 7h às 19h ocupam o mesmo espaço e nenhuma
       * hora fica de fora — o que muda é a distância entre as linhas.
       */
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: '1 1 auto',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {bloco.itens.map((hora) => (
            <div
              key={hora}
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: '1.5mm',
                flex: '1 1 0',
                minHeight: 0,
              }}
            >
              <span
                style={{
                  flex: '0 0 auto',
                  width: '9mm',
                  fontSize: `${pt}pt`,
                  lineHeight: 1,
                  fontFamily: ctx.fontes.corpo,
                  color: corRotulo,
                }}
              >
                {hora}
              </span>
              <span style={{ flex: '1 1 auto', height: 0, borderBottom: `0.18mm solid ${corLinha}` }} />
            </div>
          ))}
        </div>
      );
    }

    default:
      return null;
  }
}
